import { FormSelect, FormTextarea } from 'components/common/form'
import { Observer } from 'mobx-react-lite'
import User from 'singletons/User'
import { useFormContext, Controller } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import ErrorFieldForm from 'components/UI/ErrorFieldForm'
import formatMultiselectErrors from 'util/formatMultiselectErrors'
import {
  licensesOptions,
  pronounOptions,
  genderIdentityOptions,
  raceEthnicityOptions,
  diagnosticExpertiseOptions,
  communityExpertiseOptions,
  languagesOptions as profileLanguagesOptions,
  religiousExpertiseOptions,
} from 'constants/providerProfiles'

const AccountProfileFormContent = () => {
  const { provider } = User

  const {
    showDiagnosticExpertiseFieldInAccounts,
    showCommunityExpertiseFieldInAccounts,
    showReligiousExpertiseFieldInAccounts,
    profileReligiousExpertise,
    profileDiagnosticExpertise,
    profileCommunityExpertise,
  } = provider || {}

  const hasReligiousExpertise = profileReligiousExpertise?.length
  const hasCommunityExpertise = profileCommunityExpertise?.length
  const hasDiagnosticExpertise = profileDiagnosticExpertise?.length

  const {
    register,
    control,
    formState: { errors: initErrors },
  } = useFormContext()

  const errors = formatMultiselectErrors(initErrors)

  return (
    <>
      <h6 className="mb-4 typography-body-xl font-semibold uppercase text-neutral-500">Profile</h6>
      <div className="mb-4">
        <FormTextarea label={'Preferred Name'} autoComplete={'off'} {...register('profile.firstNamePreferred')} />
        <ErrorMessage errors={errors} name={'profile.firstNamePreferred'} as={<ErrorFieldForm />} />
      </div>
      <div className="mb-4">
        <Controller
          name={'profile.pronouns'}
          control={control}
          render={({ field }) => (
            <FormSelect
              {...field}
              label={'Pronouns*'}
              options={pronounOptions}
              disabled
              getOptionLabel={(s) => s.label}
              getOptionValue={(s) => s.value}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
        <ErrorMessage errors={errors} name={'profile.pronouns'} as={<ErrorFieldForm />} />
      </div>
      <div className="mb-4">
        <Controller
          name={'profile.raceEthnicity'}
          control={control}
          render={({ field }) => (
            <FormSelect
              {...field}
              label={'Race & Ethnicity*'}
              options={raceEthnicityOptions}
              disabled
              getOptionLabel={(s) => s.label}
              getOptionValue={(s) => s.value}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
        <ErrorMessage errors={errors} name={'profile.raceEthnicity'} as={<ErrorFieldForm />} />
      </div>
      <div className="mb-4">
        <Controller
          name={'profile.genderIdentity'}
          control={control}
          render={({ field }) => (
            <FormSelect
              {...field}
              label={'Gender Identity*'}
              options={genderIdentityOptions}
              disabled
              getOptionLabel={(s) => s.label}
              getOptionValue={(s) => s.value}
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
        <ErrorMessage errors={errors} name={'profile.genderIdentity'} as={<ErrorFieldForm />} />
      </div>
      <div className="mb-4">
        <Controller
          name={'profile.languages'}
          control={control}
          render={({ field }) => (
            <FormSelect
              {...field}
              label={'Languages*'}
              options={profileLanguagesOptions}
              getOptionLabel={(s) => s.label}
              getOptionValue={(s) => s.value}
              onChange={(e) => field.onChange(e.target.value)}
              multiple
            />
          )}
        />
        <ErrorMessage errors={errors} name={'profile.languages'} as={<ErrorFieldForm />} />
      </div>
      <Observer>
        {() =>
          !provider.isSuccessCoachProvider && (
            <>
              <div className="mb-4">
                <Controller
                  name={'profile.licenses'}
                  control={control}
                  render={({ field }) => (
                    <FormSelect
                      {...field}
                      label={'License(s)*'}
                      options={licensesOptions}
                      disabled
                      getOptionLabel={(s) => s.label}
                      getOptionValue={(s) => s.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      multiple
                    />
                  )}
                />
                <ErrorMessage errors={errors} name={'profile.licenses'} as={<ErrorFieldForm />} />
              </div>
              <Observer>
                {() =>
                  !!showDiagnosticExpertiseFieldInAccounts &&
                  !!hasDiagnosticExpertise && (
                    <div className="mb-4">
                      <Controller
                        name={'profile.diagnosticExpertise'}
                        control={control}
                        render={({ field }) => (
                          <FormSelect
                            {...field}
                            label={'Diagnostic Expertise*'}
                            options={diagnosticExpertiseOptions}
                            disabled
                            getOptionLabel={(s) => s.label}
                            getOptionValue={(s) => s.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            multiple
                          />
                        )}
                      />
                      <ErrorMessage errors={errors} name={'profile.diagnosticExpertise'} as={<ErrorFieldForm />} />
                    </div>
                  )
                }
              </Observer>
              <Observer>
                {() =>
                  !!showCommunityExpertiseFieldInAccounts &&
                  !!hasCommunityExpertise && (
                    <div className="mb-4">
                      <Controller
                        name={'profile.communityExpertise'}
                        control={control}
                        render={({ field }) => (
                          <FormSelect
                            {...field}
                            label={'Community Expertise*'}
                            options={communityExpertiseOptions}
                            disabled
                            getOptionLabel={(s) => s.label}
                            getOptionValue={(s) => s.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            multiple
                          />
                        )}
                      />
                      <ErrorMessage errors={errors} name={'profile.communityExpertise'} as={<ErrorFieldForm />} />
                    </div>
                  )
                }
              </Observer>
              <Observer>
                {() =>
                  !!showReligiousExpertiseFieldInAccounts &&
                  !!hasReligiousExpertise && (
                    <div className="mb-4">
                      <Controller
                        name={'profile.religiousExpertise'}
                        control={control}
                        render={({ field }) => (
                          <FormSelect
                            {...field}
                            label={'Religious & Spiritual Expertise*'}
                            options={religiousExpertiseOptions}
                            disabled
                            getOptionLabel={(s) => s.label}
                            getOptionValue={(s) => s.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            multiple
                          />
                        )}
                      />
                      <ErrorMessage errors={errors} name={'profile.religiousExpertise'} as={<ErrorFieldForm />} />
                    </div>
                  )
                }
              </Observer>
            </>
          )
        }
      </Observer>
      <div className="mb-4">
        <FormTextarea label={'About Me*'} rows={3} autoComplete={'off'} disabled {...register('profile.aboutMe')} />
        <ErrorMessage errors={errors} name={'profile.aboutMe'} as={<ErrorFieldForm />} />
      </div>
      <Observer>
        {() =>
          provider.isSuccessCoachProvider && (
            <>
              <div className="mb-4">
                <FormTextarea
                  label={'College / University Attended*'}
                  rows={3}
                  autoComplete={'off'}
                  disabled
                  {...register('profile.almaMater')}
                />
                <ErrorMessage errors={errors} name={'profile.almaMater'} as={<ErrorFieldForm />} />
              </div>
              <div className="mb-4">
                <FormTextarea
                  label={'Best Part of Success Coaching*'}
                  rows={3}
                  autoComplete="off"
                  disabled
                  {...register('profile.bestPartSuccessCoach')}
                />
                <ErrorMessage errors={errors} name={'profile.bestPartSuccessCoach'} as={<ErrorFieldForm />} />
              </div>
              <div className="mb-4">
                <FormTextarea
                  label={'Tips for Success*'}
                  rows={3}
                  autoComplete="off"
                  disabled
                  {...register('profile.collegeSuccessTips')}
                />
                <ErrorMessage errors={errors} name={'profile.collegeSuccessTips'} as={<ErrorFieldForm />} />
              </div>
            </>
          )
        }
      </Observer>
    </>
  )
}

export default AccountProfileFormContent
